import { usePreferredReducedMotion } from '@vueuse/core'
import { onScopeDispose, readonly, ref } from 'vue'

interface UseWheelPickerOptions {
  rowHeight: number
  visibleRows?: number
  getBounds?: () => { min: number; max: number } | undefined
  onSelect: (index: number) => void
}

/** Continuous row position and gestures; callers map the selected index to their data. */
export function useWheelPicker(options: UseWheelPickerOptions) {
  const { rowHeight, visibleRows = 3 } = options
  const position = ref(0)
  const dragging = ref(false)
  const reducedMotion = usePreferredReducedMotion()
  let frame = 0
  let wheelTimer: ReturnType<typeof setTimeout> | undefined
  let pointerId: number | undefined
  let lastY = 0
  let lastTime = 0
  let velocity = 0
  let distance = 0

  function clampPosition(value: number) {
    const bounds = options.getBounds?.()
    return bounds ? Math.max(bounds.min, Math.min(bounds.max, value)) : value
  }

  function updatePosition(value: number) {
    position.value = clampPosition(value)
    options.onSelect(Math.round(position.value))
  }

  function stopAnimation() {
    cancelAnimationFrame(frame)
    clearTimeout(wheelTimer)
  }

  function animateTo(target: number) {
    stopAnimation()
    target = clampPosition(target)
    const from = position.value
    const start = performance.now()
    const duration = Math.min(600, 180 + Math.abs(target - from) * 70)
    if (reducedMotion.value === 'reduce') {
      updatePosition(target)
      return
    }
    function tick(now: number) {
      const progress = Math.min(1, (now - start) / duration)
      updatePosition(from + (target - from) * (1 - (1 - progress) ** 3))
      if (progress < 1) {
        frame = requestAnimationFrame(tick)
      }
    }
    frame = requestAnimationFrame(tick)
  }

  function shift(direction: 1 | -1) {
    if (!dragging.value) {
      animateTo(Math.round(position.value) + direction)
    }
  }

  function onPointerDown(event: PointerEvent) {
    if (!event.isPrimary || event.button !== 0) {
      return
    }
    stopAnimation()
    const element = event.currentTarget as HTMLElement
    element.focus({ preventScroll: true })
    element.setPointerCapture(event.pointerId)
    pointerId = event.pointerId
    dragging.value = true
    lastY = event.clientY
    lastTime = event.timeStamp
    velocity = 0
    distance = 0
  }

  function onPointerMove(event: PointerEvent) {
    if (pointerId !== event.pointerId) {
      return
    }
    const delta = lastY - event.clientY
    const elapsed = Math.max(1, event.timeStamp - lastTime)
    velocity = delta / elapsed
    distance += Math.abs(delta)
    updatePosition(position.value + delta / rowHeight)
    lastY = event.clientY
    lastTime = event.timeStamp
  }

  function onPointerEnd(event: PointerEvent) {
    if (pointerId !== event.pointerId) {
      return
    }
    pointerId = undefined
    dragging.value = false
    const element = event.currentTarget as HTMLElement
    if (element.hasPointerCapture(event.pointerId)) {
      element.releasePointerCapture(event.pointerId)
    }
    if (event.type === 'pointerup' && distance < 6) {
      const offset = (event.clientY - element.getBoundingClientRect().top - (rowHeight * visibleRows) / 2) / rowHeight
      animateTo(Math.round(position.value + offset))
      return
    }
    const momentum =
      event.type === 'pointerup' && event.timeStamp - lastTime < 100 && reducedMotion.value !== 'reduce'
        ? Math.max(-5, Math.min(5, (velocity * 180) / rowHeight))
        : 0
    animateTo(Math.round(position.value + momentum))
  }

  function onWheel(event: WheelEvent) {
    if (dragging.value || !event.deltaY || event.ctrlKey) {
      return
    }
    stopAnimation()
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rowHeight * visibleRows : 1
    updatePosition(position.value + Math.max(-3, Math.min(3, (event.deltaY * unit) / rowHeight)))
    wheelTimer = setTimeout(() => animateTo(Math.round(position.value)), 120)
  }

  // Rebase the wheel without emitting a selection when its data changes externally.
  function reset() {
    stopAnimation()
    pointerId = undefined
    dragging.value = false
    position.value = 0
  }

  onScopeDispose(stopAnimation)

  return {
    position: readonly(position),
    dragging: readonly(dragging),
    shift,
    reset,
    onPointerDown,
    onPointerMove,
    onPointerEnd,
    onWheel,
  }
}
